import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BusinessInput,
    CustomDrinkInput,
    ProductInput,
    StandardDrinkInput,
} from '@barboxx/admin-components';
import {
    CREATE_MENU_ITEM_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { MenuItem } from '@barboxx/models';
import { MENU_ITEM_TYPES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateMenuItemDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();
    const { businessId } = useBusinessSelector();

    const formContext = useForm<MenuItem.CreateRequest>({
        resolver: yupResolver(CREATE_MENU_ITEM_SCHEMA),
        defaultValues: {
            isActive: true,
            type: MENU_ITEM_TYPES.StandardDrink,
            businessId,
        },
    });

    const mutateCreateMenuItem = useMutation({
        mutationKey: ['menuItem'],
        mutationFn: (data: MenuItem.CreateRequest) =>
            Api.Menu.MenuItem.postMenuItem(data),
    });

    const onSubmit = async (values: MenuItem.CreateRequest) => {
        return mutateCreateMenuItem
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created menu item!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating menu item!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    const values = useWatch(formContext) as MenuItem.CreateRequest;

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">Create Menu Item</DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <SelectElement
                        label="Type"
                        name="type"
                        options={enumToFormOptions(MENU_ITEM_TYPES)}
                        fullWidth
                    />
                    {values.type === MENU_ITEM_TYPES.StandardDrink && (
                        <StandardDrinkInput />
                    )}
                    {values.type === MENU_ITEM_TYPES.CustomDrink && (
                        <CustomDrinkInput />
                    )}
                    {values.type === MENU_ITEM_TYPES.Product && (
                        <ProductInput />
                    )}
                    <TextFieldElement
                        label="displayOrder"
                        name="displayOrder"
                        fullWidth
                    />
                    <TextFieldElement label="Notes" name="notes" fullWidth />
                    <CheckboxElement label="isActive" name="isActive" />
                    <CheckboxElement label="Featured" name="featuredItem" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
